import { useState, useEffect, ReactNode } from 'react'
import { Transition } from 'react-transition-group'
import { Flex, Close, Alert as ThemeUIAlert } from 'theme-ui'
import { Text } from 'components'
import { noop } from 'utils'

export type AlertProps = {
  open?: boolean
  children: ReactNode
  variant?: 'default' | 'info'
  autoClose?: boolean
  persistence?: number
  onClose?: () => void
}

const HIDE_AFTER_SECONDS = 7000

const defaultStyle = {
  position: 'fixed',
  bottom: 15,
  width: '100%',
  transform: 'scale(0.95)',
  transition: `300ms ease-in-out`,
  transformOrigin: 'center',
  opacity: 0,
}

const transitionStyles: any = {
  entering: {
    opacity: 0,
    transform: 'scale(0.95)',
    pointerEvents: 'none',
  },
  entered: {
    opacity: 1,
    transform: 'scale(1)',
    pointerEvents: 'auto',
    visibility: 'auto',
  },
  exiting: {
    opacity: 0,
    transform: 'scale(0.95)',
    pointerEvents: 'none',
  },
  exited: {
    opacity: 0,
    pointerEvents: 'none',
    visibility: 'hidden',
    transform: 'scale(0.95)',
  },
}

export function Alert({
  children,
  open = false,
  variant = 'default',
  autoClose = true,
  onClose = noop,
  persistence = HIDE_AFTER_SECONDS,
}: AlertProps) {
  const [show, setShow] = useState(false)
  const [transitioning, setTransitioning] = useState(false)

  useEffect(() => {
    if (transitioning) return
    if (open !== show) {
      setShow(open)
      setTransitioning(true)
      if (autoClose) {
        const timerId = setTimeout(() => {
          setTransitioning(false)
          onClose()
        }, persistence)
        return () => clearTimeout(timerId)
      }
    }
    // eslint-disable-next-line
  }, [open])
  return (
    <Transition in={transitioning} timeout={299}>
      {(state) => (
        <div
          style={{
            ...defaultStyle,
            ...transitionStyles[state],
            left: 0,
          }}>
          <Flex
            data-cy="alert"
            sx={{
              position: 'fixed',
              left: '50%',
              bottom: 0,
              width: 'calc(90% - 16px)',
              transform: 'translateX(-50%)',
              justifyContent: 'center',
            }}>
            <ThemeUIAlert variant={variant} sx={{ flex: 1 }}>
              <Text>{children}</Text>
              <Close
                ml="auto"
                mr={-2}
                type="button"
                onClick={() => {
                  setShow(false)
                  onClose()
                }}
                sx={{ cursor: 'pointer' }}
              />
            </ThemeUIAlert>
          </Flex>
        </div>
      )}
    </Transition>
  )
}
